import { Fragment, useState, useEffect } from "react";
import Form from "../../../form/Form";
import Input from "../../../form/Input";
import Button from "../../../form/Button";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { dynamicFieldAddEdit } from "../../../../services/dynamicFormService";

const DynamicNameQuestion = ({
  data = {},
  getResponse = () => {},
  close = () => {},
  nameFormat = "seperateName",
  modalType = "name", // Add modalType prop
}) => {
  const { lang } = useSelector((state) => state?.langProvider);
  const { selectedOrganizationDetails } = useSelector(
    (state) => state?.organization
  );
  const [currentNameFormat, setCurrentNameFormat] = useState(nameFormat);

  // ✅ Dynamic validation rules based on modal type and name format
  const getValidationRules = () => {
    const baseRules = {};

    if (modalType === "email") {
      baseRules.emailLabel = [
        {
          required: true,
          message: "Email label is required",
          errorType: "required",
        },
        {
          type: "range",
          max: 50,
          message: "Email label cannot exceed 50 characters",
          errorType: "warning",
        },
      ];

      baseRules.repeatEmailLabel = [
        {
          type: "range",
          max: 50,
          message: "Repeat email label cannot exceed 50 characters",
          errorType: "warning",
        },
      ];
    } else if (modalType === "name") {
      if (currentNameFormat === "seperateName") {
        baseRules.firstNameLabel = [
          {
            required: true,
            message: "First name label is required",
            errorType: "required",
          },
          {
            type: "range",
            max: 50,
            message: "First name label cannot exceed 50 characters",
            errorType: "warning",
          },
        ];

        baseRules.lastNameLabel = [
          {
            required: true,
            message: "Last name label is required",
            errorType: "required",
          },
          {
            type: "range",
            max: 50,
            message: "Last name label cannot exceed 50 characters",
            errorType: "warning",
          },
        ];
      } else {
        baseRules.fullNameLabel = [
          {
            required: true,
            message: "Full name label is required",
            errorType: "required",
          },
          {
            type: "range",
            max: 50,
            message: "Full name label cannot exceed 50 characters",
            errorType: "warning",
          },
        ];
      }
    }

    return baseRules;
  };

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
    updateErrors,
  } = useForm({
    validationRules: getValidationRules(),
  });

  // ✅ Manual validation for conditional fields
  const validateConditionalFields = () => {
    let customErrors = {};

    // Validate based on current modal type and name format
    if (modalType === "email") {
      if (!values?.emailLabel?.trim()) {
        customErrors.emailLabel = {
          message: "Email label is required",
          type: "required",
        };
      } else if (values.emailLabel.length > 50) {
        customErrors.emailLabel = {
          message: "Email label cannot exceed 50 characters",
          type: "warning",
        };
      }
    } else if (modalType === "name") {
      if (currentNameFormat === "seperateName") {
        if (!values?.firstNameLabel?.trim()) {
          customErrors.firstNameLabel = {
            message: "First name label is required",
            type: "required",
          };
        } else if (values.firstNameLabel.length > 50) {
          customErrors.firstNameLabel = {
            message: "First name label cannot exceed 50 characters",
            type: "warning",
          };
        }

        if (!values?.lastNameLabel?.trim()) {
          customErrors.lastNameLabel = {
            message: "Last name label is required",
            type: "required",
          };
        } else if (values.lastNameLabel.length > 50) {
          customErrors.lastNameLabel = {
            message: "Last name label cannot exceed 50 characters",
            type: "warning",
          };
        }
      } else {
        if (!values?.fullNameLabel?.trim()) {
          customErrors.fullNameLabel = {
            message: "Full name label is required",
            type: "required",
          };
        } else if (values.fullNameLabel.length > 50) {
          customErrors.fullNameLabel = {
            message: "Full name label cannot exceed 50 characters",
            type: "warning",
          };
        }
      }
    }

    return customErrors;
  };

  // Initialize form values based on modal type
  useEffect(() => {
    if (modalType === "email") {
      setFieldsValue({
        emailLabel: data?.label || "Email",
        repeatEmailLabel: "Repeat Email",
      });
    } else if (modalType === "name") {
      const name = data?.label;
      console.log({ name });
      setCurrentNameFormat(data?.defaultValue || "seperateName");
      if (data?.defaultValue === "seperateName") {
        // Check if label contains '|'
        if (typeof name === "string" && name.includes("|")) {
          const [firstName, lastName] = name.split("|").map((s) => s.trim());
          console.log({ firstName, lastName });

          setFieldsValue({
            firstNameLabel: firstName || "First Name",
            lastNameLabel: lastName || "Last Name",
          });
        } else {
          setFieldsValue({
            firstNameLabel: "First Name",
            lastNameLabel: "Last Name",
          });
        }
      } else {
        setFieldsValue({
          fullNameLabel: name || "Full Name",
        });
      }
    }
  }, [modalType, data]);

  // ✅ Real-time validation for conditional fields
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const conditionalErrors = validateConditionalFields();

      const fieldsToUpdate = [
        "emailLabel",
        "repeatEmailLabel",
        "firstNameLabel",
        "lastNameLabel",
        "fullNameLabel",
      ];
      const updatedErrors = {};

      fieldsToUpdate.forEach((field) => {
        if (conditionalErrors[field]) {
          updatedErrors[field] = conditionalErrors[field];
        } else {
          updatedErrors[field] = undefined;
        }
      });

      updateErrors(updatedErrors);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    values.emailLabel,
    values.repeatEmailLabel,
    values.firstNameLabel,
    values.lastNameLabel,
    values.fullNameLabel,
    modalType,
    currentNameFormat,
  ]);

  const { mutate: onSubmit, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const finalPayload =
        modalType === "email"
          ? {
              label: { text: { [lang]: val?.emailLabel } },
              placeholder: { [lang]: `Enter ${val?.emailLabel}` },
              type: "email",
              required: true,
            }
          : currentNameFormat === "seperateName"
          ? {
              label: {
                text: {
                  [lang]: val?.firstNameLabel + " | " + val?.lastNameLabel,
                },
              },
              placeholder: {
                [lang]: `Enter ${val?.firstNameLabel} ${val?.lastNameLabel}`,
              },
              type: "input",
              required: true,
              defaultValue: currentNameFormat,
            }
          : {
              label: { text: { [lang]: val?.fullNameLabel } },
              placeholder: { [lang]: `Enter ${val?.fullNameLabel}` },
              type: "input",
              required: true,
              defaultValue: currentNameFormat,
            };

      if (data?.isEdit && data?.id) {
        finalPayload.id = data.id;
        finalPayload.organization = selectedOrganizationDetails?.data?._id;
      }

      return await dynamicFieldAddEdit(finalPayload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        getResponse(res);
        close();
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
      toast.error("Failed to save question. Please try again.");
    },
  });

  const handleNameFormatChange = (value) => {
    setCurrentNameFormat(value);

    // Reset form values when format changes
    if (value === "seperateName") {
      setFieldsValue({
        firstNameLabel: "First name",
        lastNameLabel: "Last name",
      });
    } else {
      setFieldsValue({
        fullNameLabel: "Full name",
      });
    }

    // Clear errors when format changes
    updateErrors({
      firstNameLabel: undefined,
      lastNameLabel: undefined,
      fullNameLabel: undefined,
    });
  };

  // ✅ Custom form validation before submission
  const validateFormData = () => {
    const conditionalErrors = validateConditionalFields();

    if (Object.keys(conditionalErrors).length > 0) {
      updateErrors(conditionalErrors);
      return false;
    }

    return true;
  };

  // ✅ Custom form submission with validation
  const handleFormSubmit = (formValues) => {
    if (validateFormData()) {
      onSubmit(formValues);
    }
  };

  // Render Email Question Form
  const renderEmailForm = () => (
    <div className="space-y-4">
      <div>
        <Input
          label="Label"
          inputPlaceholder="Enter email label"
          inputName="emailLabel"
          value={values?.emailLabel || ""}
          onChange={handleChange}
          {...formValidate(errors, "emailLabel")}
        />
      </div>

      {/* <div>
        <Checkbox
          checkboxLabel="Customers have to enter their email twice to avoid typos"
          checkboxName="emailRepeat"
          checked={emailRepeatEnabled}
          onChange={handleEmailRepeatChange}
        />
      </div>

      {emailRepeatEnabled && (
        <div>
          <Input
            label="Repeat email label"
            inputPlaceholder="Enter repeat email label"
            inputName="repeatEmailLabel"
            value={values?.repeatEmailLabel || ""}
            onChange={handleChange}
            {...formValidate(errors, "repeatEmailLabel")}
          />
          {errors?.repeatEmailLabel?.message && (
            <p className="mt-1 text-sm text-red-600">
              {errors.repeatEmailLabel.message}
            </p>
          )}
        </div>
      )} */}
    </div>
  );

  // Render Name Question Form
  const renderNameForm = () => (
    <div className="space-y-4">
      <div>
        <Select
          label="Format"
          placeholder="Select format"
          dropdownData={[
            {
              name: "First name, Last name",
              value: "seperateName",
            },
            {
              name: "Full name",
              value: "fullname",
            },
          ]}
          selectedValue={currentNameFormat}
          onChange={handleNameFormatChange}
        />
      </div>

      {currentNameFormat === "seperateName" ? (
        <Fragment>
          <div>
            <Input
              label="First name label"
              inputPlaceholder="Enter first name label"
              inputName="firstNameLabel"
              value={values?.firstNameLabel || ""}
              onChange={handleChange}
              {...formValidate(errors, "firstNameLabel")}
            />
          </div>
          <div>
            <Input
              label="Last name label"
              inputPlaceholder="Enter last name label"
              inputName="lastNameLabel"
              value={values?.lastNameLabel || ""}
              onChange={handleChange}
              {...formValidate(errors, "lastNameLabel")}
            />
          </div>
        </Fragment>
      ) : (
        <div>
          <Input
            label="Full name label"
            inputPlaceholder="Enter full name label"
            inputName="fullNameLabel"
            value={values?.fullNameLabel || ""}
            onChange={handleChange}
            {...formValidate(errors, "fullNameLabel")}
          />
          {/* Manual error display for fullNameLabel */}
          {errors?.fullNameLabel?.message && (
            <p className="mt-1 text-sm text-red-600">
              {errors.fullNameLabel.message}
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Form onSubmit={handleFormSubmit} handleSubmit={handleSubmit}>
      <div className="space-y-6">
        {/* Render form based on modal type */}
        {modalType === "email" ? renderEmailForm() : renderNameForm()}

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4 pt-4">
          <Button
            buttonLabel="Cancel"
            variant="outline"
            buttonFunction={close}
          />
          <Button
            buttonType="submit"
            buttonLabel="Save question"
            variant="primary"
            {...(isLoading && {
              loading: true,
              isDisable: true,
            })}
          />
        </div>
      </div>
    </Form>
  );
};

export default DynamicNameQuestion;
